import { Container, Sprite, useApp } from "@pixi/react"
import { InfoTwoToneIcon } from "@relex-solutions/fragments"
import type { DisplayObject, Container as PixiContainer } from "pixi.js"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  getBorderColor,
  HIGHLIGHT_NODE_COLOR,
  ICON_COLORS,
  LABEL_BG_COLOR_MAP,
  NODE_BORDER_RADIUS,
  NODE_LABELS,
  nodeTextStyle,
  PADDING_X,
  PADDING_Y
} from "@/domains/visualization/constants/graph"
import { usePulseAnimation } from "@/domains/visualization/hooks/usePulseAnimation/usePulseAnimation.ts"
import { useTextBoxTexture } from "@/domains/visualization/hooks/useTextBoxTexture/useTextBoxTexture"
import {
  selectCurrentNodeId,
  selectDisableInteractions,
  selectRootCustomerId,
  selectSetHoveredNode,
  selectSidebarLockedNode,
  selectToggleSidebarLock,
  selectToggleVertexSelection
} from "@/domains/visualization/store/selectors"
import { useGraphStore } from "@/domains/visualization/store/store"
import type { NodeLabel, PositionedNode } from "@/domains/visualization/types/Graph"
import { NodeIconTooltip } from "../NodeIconTooltip/NodeIconTooltip.tsx"
import { NodeLabelBadge } from "../NodeLabelBadge/NodeLabelBadge.tsx"

interface GraphNodeProps {
  node: PositionedNode
  isLoading?: boolean
  onNodeClick?: (containerRef: DisplayObject) => void
}

export const GraphNode = memo(function GraphNode({ node, isLoading = false, onNodeClick }: GraphNodeProps) {
  const app = useApp()
  const { t } = useTranslation()

  const rootCustomerId = useGraphStore(selectRootCustomerId)
  const toggleVertexSelection = useGraphStore(selectToggleVertexSelection)
  const currentNodeId = useGraphStore(selectCurrentNodeId)
  const setHoveredNode = useGraphStore(selectSetHoveredNode)
  const sidebarLockedNode = useGraphStore(selectSidebarLockedNode)
  const toggleSidebarLock = useGraphStore(selectToggleSidebarLock)
  const disableInteractions = useGraphStore(selectDisableInteractions)

  const containerRef = useRef<PixiContainer | null>(null)
  const isClicked = useRef(false)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isHovered, setIsHovered] = useState(false)

  const isCurrentNode = node.id === currentNodeId
  const isNodeClickable = !isClicked.current || isCurrentNode

  // Use the pulse animation hook
  const { alpha: pulseAlpha, setEnabled: setPulseEnabled } = usePulseAnimation({
    speed: 0.02,
    enabled: isLoading && isCurrentNode && !disableInteractions
  })

  // Update pulse animation state when loading or current node state changes
  useEffect(() => {
    setPulseEnabled(isLoading && isCurrentNode && !disableInteractions)
  }, [isLoading, isCurrentNode, disableInteractions, setPulseEnabled])

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  const shouldUseBillingEntityColor = node.label === NODE_LABELS.CUSTOMER && node.subType === "Billing entity"
  const effectiveLabel = shouldUseBillingEntityColor ? NODE_LABELS.BILLING_ENTITY : (node.label as NodeLabel)
  const backgroundColor = rootCustomerId === node.id ? HIGHLIGHT_NODE_COLOR : LABEL_BG_COLOR_MAP[effectiveLabel]

  // Apply pulsing effect to border when loading and is current node
  const borderColor = getBorderColor(effectiveLabel, isCurrentNode, rootCustomerId === node.id)
  const borderAlpha = isLoading && isCurrentNode && !disableInteractions ? pulseAlpha : 1

  const textBoxTextureOptions = {
    title: node.name,
    app,
    fontStyle: nodeTextStyle,
    backgroundColor,
    textColor: nodeTextStyle.fill,
    borderRadius: NODE_BORDER_RADIUS,
    borderColor,
    borderAlpha,
    borderWidth: disableInteractions ? 0 : 3,
    paddingX: PADDING_X,
    paddingY: PADDING_Y,
    minWidth: node.width
  }

  const { texture, scale, offsetX, offsetY } = useTextBoxTexture(textBoxTextureOptions)

  const handleClick = () => {
    if (!isNodeClickable || disableInteractions || !containerRef.current) return
    isClicked.current = !isClicked.current
    toggleVertexSelection(node.id)
    onNodeClick?.(containerRef.current)
  }

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      // Only hide if this node is still the currently hovered node
      const currentHovered = useGraphStore.getState().hoveredNode
      if (currentHovered?.id === node.id) {
        setHoveredNode(null)
      }
    }, 500)
  }, [clearHideTimeout, setHoveredNode, node.id])

  const handlePointerOver = useCallback(() => {
    if (!disableInteractions) {
      clearHideTimeout()
      setIsHovered(true)
    }
  }, [disableInteractions, clearHideTimeout])

  const handlePointerOut = useCallback(() => {
    setIsHovered(false)
    scheduleHide()
  }, [scheduleHide])

  const handleTooltipHover = useCallback(() => {
    clearHideTimeout()
    setHoveredNode(node)
  }, [clearHideTimeout, node, setHoveredNode])

  const handleTooltipLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const handleIconClick = useCallback(() => {
    toggleSidebarLock(node)
  }, [toggleSidebarLock, node])

  return (
    <Container
      ref={containerRef}
      x={node.x}
      y={node.y}
      eventMode={disableInteractions ? "none" : "static"}
      pointerdown={handleClick}
      pointerover={handlePointerOver}
      pointerout={handlePointerOut}
      cursor={disableInteractions || !isNodeClickable ? "default" : "pointer"}
      name={node.id}
    >
      {texture && <Sprite texture={texture} scale={scale} x={-offsetX} y={-offsetY} />}
      <NodeLabelBadge
        // TODO: Use translation for all node labels instead of raw node.label
        label={shouldUseBillingEntityColor ? t("visualization.billingEntity") : node.label}
        x={node.width}
        y={-4}
      />
      {!disableInteractions && (
        <NodeIconTooltip
          FragmentsIconComponent={InfoTwoToneIcon}
          isActive={sidebarLockedNode?.id === node.id}
          size={24}
          colors={{
            primaryColor: ICON_COLORS.PRIMARY,
            secondaryColor: ICON_COLORS.SECONDARY
          }}
          onIconClick={handleIconClick}
          x={20}
          y={-4}
          visible={isHovered || sidebarLockedNode?.id === node.id}
          onTooltipHover={handleTooltipHover}
          onTooltipLeave={handleTooltipLeave}
        />
      )}
    </Container>
  )
})
